// pattern: Imperative Shell
// Dashboard view — displays teacher's quizzes

import { createElement, addListener, clearElement } from '../shell/dom.ts';
import { getDashboard, getQuiz } from '../shell/api-client.ts';
import { navigate, routeToHash } from '../shell/router.ts';
import type { StoredQuiz } from '../core/question-model.ts';

type QuizWithId = { readonly quiz: StoredQuiz; readonly id: string };

export function renderDashboard(container: HTMLElement, dashboardId: string): () => void {
  const cleanups: Array<() => void> = [];
  const loadedQuizzes: Array<QuizWithId> = [];
  let filteredQuizzes: Array<QuizWithId> = [];

  // Show loading state
  const loadingDiv = createElement('div', { className: 'text-center dashboard-loading' });

  const spinner = createElement('div', { className: 'spinner' });
  const loadingText = createElement('p', {
    textContent: 'Loading dashboard...',
    className: 'text-secondary dashboard-loading-text',
  });

  loadingDiv.append(spinner, loadingText);
  container.appendChild(loadingDiv);

  // Fetch dashboard data
  void (async () => {
    const dashboardResult = await getDashboard(dashboardId);

    if (!dashboardResult.ok) {
      clearElement(loadingDiv);
      const errorHeading = createElement('h2', { textContent: 'Dashboard Not Found' });
      const errorMsg = createElement('p', { className: 'text-secondary dashboard-error-msg' });
      errorMsg.textContent =
        dashboardResult.error || 'This dashboard does not exist or has been removed.';
      const homeLink = createElement('a', {
        textContent: 'Go Home',
        className: 'btn btn-primary',
      });
      homeLink.setAttribute('href', '#/');
      loadingDiv.className = 'card text-center';
      loadingDiv.style.marginTop = 'var(--spacing-2xl)';
      loadingDiv.append(errorHeading, errorMsg, homeLink);
      return;
    }

    const dashboard = dashboardResult.data;

    // Fetch all quizzes
    const quizzes: Array<QuizWithId> = [];
    for (const quizId of dashboard.quizIds) {
      const quizResult = await getQuiz(quizId);
      if (quizResult.ok) {
        quizzes.push({ quiz: quizResult.data, id: quizId });
      }
    }

    // Sort by createdAt descending (newest first)
    quizzes.sort((a, b) => b.quiz.createdAt - a.quiz.createdAt);
    loadedQuizzes.push(...quizzes);
    filteredQuizzes = [...quizzes];

    // Clear loading state and render content
    clearElement(container);
    renderContent();
  })();

  function renderContent(): void {
    // Header
    const header = createElement('div', { className: 'dashboard-header' });

    const title = createElement('h1', { textContent: 'My Quizzes', className: 'dashboard-title' });

    const controls = createElement('div', { className: 'dashboard-controls' });

    const searchInput = createElement('input', {
      type: 'text',
      className: 'input',
      placeholder: 'Search quizzes...',
    });

    const newBtn = createElement('button', {
      className: 'btn btn-primary',
      textContent: '+ New Quiz',
    });

    const newCleanup = addListener(newBtn, 'click', () => {
      navigate(routeToHash({ type: 'upload', dashboardId }));
    });
    cleanups.push(newCleanup);

    controls.append(searchInput, newBtn);
    header.append(title, controls);
    container.appendChild(header);

    // Search handler
    const searchCleanup = addListener(searchInput, 'input', () => {
      const term = searchInput.value.toLowerCase();
      filteredQuizzes = term
        ? loadedQuizzes.filter((q) => q.quiz.name.toLowerCase().includes(term))
        : [...loadedQuizzes];
      renderQuizList();
    });
    cleanups.push(searchCleanup);

    // Quiz list container
    const listContainer = createElement('div', { className: 'quiz-list dashboard-list' });
    container.appendChild(listContainer);

    // Dashboard link section
    const linkSection = createElement('div', { className: 'dashboard-link-section' });

    const linkLabel = createElement('p', {
      textContent: '─── Your dashboard link ───',
      className: 'dashboard-link-label',
    });

    const copyDashboardBtn = createElement('button', {
      className: 'btn btn-secondary dashboard-copy-btn',
      textContent: 'Copy dashboard link',
    });

    const copyDashboardCleanup = addListener(copyDashboardBtn, 'click', () => {
      const dashboardUrl = `${window.location.origin}${window.location.pathname}#/t/${dashboardId}`;
      void navigator.clipboard.writeText(dashboardUrl);
    });
    cleanups.push(copyDashboardCleanup);

    linkSection.append(linkLabel, copyDashboardBtn);
    container.appendChild(linkSection);

    // Initial render
    renderQuizList();

    function renderQuizList(): void {
      clearElement(listContainer);

      if (filteredQuizzes.length === 0) {
        if (loadedQuizzes.length === 0) {
          const emptyMsg = createElement('p', {
            textContent: 'Create your first quiz!',
            className: 'text-center text-secondary dashboard-empty',
          });
          listContainer.appendChild(emptyMsg);
        }
        return;
      }

      for (const { quiz, id: quizId } of filteredQuizzes) {
        const card = createElement('div', { className: 'card quiz-card' });
        card.setAttribute('data-quiz-card', '');

        const nameEl = createElement('h3', { textContent: quiz.name, className: 'quiz-card-name' });

        const infoEl = createElement('p', {
          textContent: `${quiz.questions.length} questions · ${new Date(quiz.createdAt).toLocaleDateString()}`,
          className: 'text-secondary quiz-card-info',
        });

        const actions = createElement('div', { className: 'quiz-card-actions' });

        const editBtn = createElement('button', {
          className: 'btn btn-secondary',
          textContent: 'Edit',
        });

        const editCleanup = addListener(editBtn, 'click', () => {
          navigate(routeToHash({ type: 'editor', dashboardId, quizId }));
        });
        cleanups.push(editCleanup);

        const shareBtn = createElement('button', {
          className: 'btn btn-secondary',
          textContent: 'Share link',
        });

        const shareCleanup = addListener(shareBtn, 'click', () => {
          const quizUrl = `${window.location.origin}${window.location.pathname}?q=${quizId}`;
          void navigator.clipboard.writeText(quizUrl);
        });
        cleanups.push(shareCleanup);

        actions.append(editBtn, shareBtn);
        card.append(nameEl, infoEl, actions);
        listContainer.appendChild(card);
      }
    }
  }

  return () => cleanups.forEach((fn) => fn());
}
